/**
 * Sign-in for LGU staff and merchant users: e-mail and password, then the authenticator
 * code. A first sign-in asks for the user's own password, then (where the role needs it)
 * enrolment of an authenticator; recovery codes are shown once.
 */
import { useMutation } from '@tanstack/react-query';
import { KeyRound, Landmark } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, ErrorNote, Field, Note } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth, type SessionResponse } from '../../lib/auth';
import { useI18n } from '../../lib/i18n';
import { usePublicSettings } from '../../lib/queries';

type LoginResponse = ({ mfaRequired: true; mfaToken: string }) | ({ mfaRequired: false } & SessionResponse);
interface Setup { secret: string; otpauthUri: string; qrDataUrl: string }

export function Frame({ title, sub, children, wide }: { title: string; sub?: ReactNode; children: ReactNode; wide?: boolean }) {
  const s = usePublicSettings();
  return (
    <div className="grid min-h-dvh place-items-center bg-paper px-4 py-10">
      <div className={wide ? 'w-full max-w-lg' : 'w-full max-w-sm'}>
        <div className="mb-6 flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand text-brand-ink"><Landmark aria-hidden className="h-6 w-6" /></span>
          <div>
            <p className="display text-xl leading-tight">{s.data?.lguName ?? 'LGU Aid'}</p>
            <p className="text-sm text-muted">Constituent ID and assistance</p>
          </div>
        </div>
        <div className="rounded-ticket border border-line bg-card p-5 shadow-lift">
          <h1 className="text-xl font-bold">{title}</h1>
          {sub && <div className="mt-1 text-sm text-muted">{sub}</div>}
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

const codeInput = (v: string) => v.replace(/[^\dA-Za-z-]/g, '').slice(0, 20);

export function SignIn(): JSX.Element {
  const { adopt } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const login = useMutation({
    mutationFn: () => api<LoginResponse>('/auth/login', { method: 'POST', body: { email: email.trim(), password }, anonymous: true }),
    onSuccess: (r) => { if (r.mfaRequired) setMfaToken(r.mfaToken); else adopt(r); },
  });
  const verify = useMutation({
    mutationFn: () => api<SessionResponse>('/auth/mfa', { method: 'POST', body: { mfaToken, code: code.trim() }, anonymous: true }),
    onSuccess: adopt,
  });

  if (mfaToken) {
    return (
      <Frame title={t('Authenticator code')} sub={t('Open your authenticator app and enter the 6-digit code. Lost your phone? Enter one of your recovery codes.')}>
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); verify.mutate(); }} className="space-y-4">
          <Field label={t('Code')} autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(codeInput(e.target.value))} placeholder="123456" />
          <ErrorNote error={verify.error} />
          <Button type="submit" block loading={verify.isPending} disabled={code.length < 6}>{t('Verify')}</Button>
          <button type="button" className="min-h-11 w-full text-sm font-semibold text-brand" onClick={() => { setMfaToken(null); setCode(''); verify.reset(); }}>{t('Start again')}</button>
        </form>
      </Frame>
    );
  }

  return (
    <Frame title={t('Sign in')} sub={t('For LGU staff and accredited merchants. Constituents use their card and PIN at the store.')}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); login.mutate(); }} className="space-y-4">
        <Field label={t('E-mail')} type="email" autoComplete="username" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field label={t('Password')} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <ErrorNote error={login.error} />
        <Button type="submit" block loading={login.isPending} disabled={!email || !password} icon={<KeyRound aria-hidden className="h-4 w-4" />}>{t('Sign in')}</Button>
      </form>
      <p className="mt-4 text-sm text-muted">{t('Forgot your password? Ask your administrator (LGU staff) or your store owner (cashiers).')}</p>
      <div className="mt-4 border-t border-line pt-4 text-sm">
        <Link to="/merchant-signup" className="font-semibold text-brand underline-offset-2 hover:underline">{t('Store owner? Apply for accreditation')}</Link>
        <span className="mx-2 text-muted">|</span>
        <Link to="/directory" className="font-semibold text-brand underline-offset-2 hover:underline">{t('Where can I use my card?')}</Link>
      </div>
    </Frame>
  );
}

/** First sign-in: choose your own password (FR-ADM-01). */
export function SetPassword(): JSX.Element {
  const { user, signOut } = useAuth();
  const { t } = useI18n();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const change = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>('/auth/password', { method: 'POST', body: { currentPassword: current, newPassword: next } }),
    onSuccess: () => void signOut(),
  });
  const mismatch = again.length > 0 && next !== again;
  return (
    <Frame title={t('Choose your password')} sub={t('{name}, replace the temporary password with your own. You will then sign in again.', { name: user?.name ?? '' })}>
      <form onSubmit={(e) => { e.preventDefault(); change.mutate(); }} className="space-y-4">
        <Field label={t('Temporary password')} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        <Field label={t('New password')} type="password" autoComplete="new-password" hint={t('At least 10 characters. A short sentence is easy to remember.')} value={next} onChange={(e) => setNext(e.target.value)} />
        <Field label={t('New password again')} type="password" autoComplete="new-password" value={again} error={mismatch ? t('The passwords do not match.') : undefined} onChange={(e) => setAgain(e.target.value)} />
        <ErrorNote error={change.error} />
        <Button type="submit" block loading={change.isPending} disabled={!current || next.length < 10 || next !== again}>{t('Save password')}</Button>
        <button type="button" className="min-h-11 w-full text-sm font-semibold text-brand" onClick={() => void signOut()}>{t('Sign out')}</button>
      </form>
    </Frame>
  );
}

/** Authenticator enrolment, required for approval, finance, admin and store-owner roles. */
export function MfaSetup(): JSX.Element {
  const { adopt, signOut } = useAuth();
  const { t } = useI18n();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<({ recoveryCodes: string[] } & SessionResponse) | null>(null);
  const setup = useMutation({ mutationFn: () => api<Setup>('/auth/mfa/setup', { method: 'POST', body: {} }) });
  const enable = useMutation({
    mutationFn: () => api<{ recoveryCodes: string[] } & SessionResponse>('/auth/mfa/enable', { method: 'POST', body: { code: code.trim() } }),
    onSuccess: (r) => (r.recoveryCodes?.length ? setResult(r) : adopt(r)),
  });

  if (result) {
    const copy = () => void navigator.clipboard?.writeText(result.recoveryCodes.join('\n')).catch(() => undefined);
    return (
      <Frame title={t('Save your recovery codes')} sub={t('Each code works once if you lose your phone. They are shown only now. Keep them somewhere safe, away from your phone.')}>
        <ul className="grid grid-cols-2 gap-2 rounded-xl bg-paper p-3 font-mono text-sm">
          {result.recoveryCodes.map((c) => <li key={c} className="tabular">{c}</li>)}
        </ul>
        <div className="mt-4 flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={copy}>{t('Copy')}</Button>
          <Button className="flex-1" onClick={() => adopt(result)}>{t('I saved them')}</Button>
        </div>
      </Frame>
    );
  }
  if (!setup.data) {
    return (
      <Frame title={t('Set up two-step sign-in')} sub={t('Your role can approve, pay or manage, so it needs an authenticator app (Google Authenticator, Microsoft Authenticator…). It takes a minute.')}>
        <ErrorNote error={setup.error} />
        <Button block className="mt-2" loading={setup.isPending} onClick={() => setup.mutate()}>{t('Start set-up')}</Button>
        <button type="button" className="mt-3 min-h-11 w-full text-sm font-semibold text-brand" onClick={() => void signOut()}>{t('Sign out')}</button>
      </Frame>
    );
  }
  const s = setup.data;
  return (
    <Frame title={t('Scan with your authenticator')} sub={t('Scan the code, then enter the 6 digits the app shows.')}>
      <img src={s.qrDataUrl} alt={t('QR code for your authenticator app')} width={200} height={200} className="mx-auto rounded-xl bg-white p-2" />
      <Note tone="neutral">{t("Can't scan? Enter this key in the app:")} <code className="break-all font-mono text-ink">{s.secret}</code></Note>
      <form onSubmit={(e) => { e.preventDefault(); enable.mutate(); }} className="mt-4 space-y-4">
        <Field label={t('6-digit code')} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        <ErrorNote error={enable.error} />
        <Button type="submit" block loading={enable.isPending} disabled={code.length !== 6}>{t('Turn on')}</Button>
      </form>
    </Frame>
  );
}
